/**
 * 再認証の共通ハンドリング
 *
 * `@SecureApi` を付与した自社エンドポイントは、AAL / 直近の認証要件を満たさない
 * リクエストを `HTTP 401 + code` で弾く:
 * - `REAUTH_REQUIRED` … AAL1 鮮度切れ → パスワード再入力（refresh）
 * - `AAL2_REQUIRED` / `MFA_REAUTH_REQUIRED` … AAL2 / MFA → aal2 モーダル
 *
 * その 401 を共通の認証エラーハンドラ (handleAuthErrorResponse) が `ReauthRequiredError`
 * に変換し、機密操作の呼び出しを `handleApiErrorWithReauth` でラップしておくと、
 * 再認証モーダルを表示 → 成功後に元操作を再実行する。
 *
 * Ory 用の `handleOryErrorWithReauth` (lib/auth/ory/service) と対になる、自社 API 用。
 * transport 非依存のため openapi-fetch / oRPC いずれの呼び出しにも使える。
 */
import { ERROR_CODE } from './error-code';
import { ReauthRequiredError, type ReauthAuthType } from './error.reauth-required';
const REAUTH_REQUIRED_CODES: ReadonlySet<string> = new Set([ERROR_CODE.AAL2_REQUIRED, ERROR_CODE.MFA_REAUTH_REQUIRED, ERROR_CODE.REAUTH_REQUIRED]);

/**
 * 再認証を要求するエラーコードかどうかを判定する。
 */
export const isReauthRequiredCode = (code: unknown): boolean => typeof code === 'string' && REAUTH_REQUIRED_CODES.has(code);

/** エラーコードから再認証モーダルの authType を決める。 */
export function resolveReauthAuthType(code: unknown): ReauthAuthType {
  return code === ERROR_CODE.REAUTH_REQUIRED ? 'refresh' : 'aal2';
}

/**
 * 進行中の再認証フロー。`GlobalAuthModal` は単一の resolver しか保持しないため、
 * 複数の機密操作が同時に `ReauthRequiredError` で失敗しても openModal は一度だけ呼び、
 * 全 caller が同じ Promise を待つ（集約しないと先発の Promise が後発の openModal で
 * 上書きされ宙吊りになる）。成功・キャンセルいずれの settle 後も null に戻す。
 *
 * authType が衝突した場合（refresh と aal2 が同時）は、より強い aal2 を優先する。
 */
let inflightReauth: {
  promise: Promise<void>;
  authType: ReauthAuthType;
} | null = null;

/**
 * 再認証モーダルを開いて完了を待つ。401 を受けてからの再認証だけでなく、事前チェック
 * （`GET /v2/auth/reauth-requirements`）が未充足を返したときの導線からも使う。
 * キャンセル時は `AuthCancelledError` で reject する。
 */
export const requestReauth = (authType: ReauthAuthType, message?: string): Promise<void> => {
  if (inflightReauth != null) {
    // 既に aal2 が開いていればそのまま待つ。refresh 中に aal2 が来たら昇格はせず
    // 既存フロー完了を待つ（稀な衝突。先発完了後に後続が再 throw → 再挑戦）。
    return inflightReauth.promise;
  }
  const promise = (async () => {
    const {
      useGlobalAuthModal
    } = await import('@/features/auth/components/GlobalAuthModal');
    await useGlobalAuthModal.getState().openModal(authType, {
      message
    });
  })().finally(() => {
    inflightReauth = null;
  });
  inflightReauth = {
    promise,
    authType
  };
  return promise;
};

/**
 * API 呼び出しが `ReauthRequiredError`（@SecureApi の 401 + code）で失敗した場合に、
 * 再認証モーダルを開き、成功後に元の呼び出しを **1回だけ** 再実行する。
 *
 * @example
 * export const updatePassword = (newPassword: string) =>
 *   handleApiErrorWithReauth(async () => {
 *     await orpcClient.authPassword.updatePassword({ newPassword });
 *   });
 */
export async function handleApiErrorWithReauth<T>(apiCall: () => Promise<T>): Promise<T> {
  try {
    return await apiCall();
  } catch (error) {
    if (!(error instanceof ReauthRequiredError)) {
      throw error;
    }
    await requestReauth(error.authType, error.displayMessage);
    return await apiCall();
  }
}